import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { Reducer } from "redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import { sessionSlice, sessionReducer } from "./slices/session.slice";
import { authApi } from "./apis/auth.api";
import { adminApi } from "./apis/admin.api";
import { restaurantApi } from "./apis/restaurant.api";
import { cityApi } from "./apis/city.api";
import { modifierOptionApi } from "./apis/modifier-option.api";
import { modifierGroupApi } from "./apis/modifier-group.api";
import { itemApi } from "./apis/item.api";
import { headerApi } from "./apis/header.api";
import { riderApi } from "./apis/rider.api";
import { orderApi } from "./apis/order.api";
import { riderRequestApi } from "./apis/rider-request.api";
import { restaurantRequestApi } from "./apis/restaurant-request.api";
import { adApi } from "./apis/ad.api";

const reducers = {
  [sessionSlice.name]: sessionReducer,
  [authApi.reducerPath]: authApi.reducer,
  [adminApi.reducerPath]: adminApi.reducer,
  [restaurantApi.reducerPath]: restaurantApi.reducer,
  [cityApi.reducerPath]: cityApi.reducer,
  [modifierOptionApi.reducerPath]: modifierOptionApi.reducer,
  [modifierGroupApi.reducerPath]: modifierGroupApi.reducer,
  [itemApi.reducerPath]: itemApi.reducer,
  [headerApi.reducerPath]: headerApi.reducer,
  [riderApi.reducerPath]: riderApi.reducer,
  [orderApi.reducerPath]: orderApi.reducer,
  [riderRequestApi.reducerPath]: riderRequestApi.reducer,
  [restaurantRequestApi.reducerPath]: restaurantRequestApi.reducer,
  [adApi.reducerPath]: adApi.reducer,
};

const combinedReducer = combineReducers<typeof reducers>(reducers);

export const rootReducer: Reducer<RootState> = (state, action) => {
  return combinedReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      authApi.middleware,
      adminApi.middleware,
      restaurantApi.middleware,
      cityApi.middleware,
      modifierOptionApi.middleware,
      modifierGroupApi.middleware,
      itemApi.middleware,
      headerApi.middleware,
      riderApi.middleware,
      orderApi.middleware,
      riderRequestApi.middleware,
      restaurantRequestApi.middleware,
      adApi.middleware,
    ]);
  },
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof combinedReducer>;
export const useTypedDispatch = () => useDispatch<AppDispatch>();
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

export {
  logout,
  setAdmin,
  setCity,
  setRestaurant,
} from "./slices/session.slice";

export { useLoginMutation } from "./apis/auth.api";

export {
  useFetchAdminsQuery,
  useToggleAdminIsActiveStatusMutation,
  useCreateAdminMutation,
  useDeleteAdminMutation,
  useFetchAdminDetailsQuery,
  useUpdateAdminMutation,
} from "./apis/admin.api";

export {
  useFetchRestaurantsQuery,
  useToggleRestaurantIsActiveStatusMutation,
  useCreateRestaurantMutation,
  useFetchRestaurantDetailsQuery,
  useUpdateRestaurantMutation,
  useUpdateRestaurantLogoMutation,
  useUpdateRestaurantBannerMutation,
  useFetchAllRestaurantsQuery,
  useDeleteRestaurantMutation,
} from "./apis/restaurant.api";

export {
  useFetchAllCitiesQuery,
  useFetchCitiesQuery,
  useFetchCityDetailsQuery,
  useCreateCityMutation,
  useUpdateCityMutation,
  useToggleCityIsActiveStatusMutation,
  useDeleteCityMutation,
} from "./apis/city.api";

export {
  useFetchModifierOptionsQuery,
  useToggleModifierOptionStockMutation,
  useCreateModifierOptionMutation,
  useUpdateModifierOptionMutation,
  useFetchModifierOptionDetailsQuery,
  useDeleteModifierOptionMutation,
  useFetchAllModifierOptionsQuery,
} from "./apis/modifier-option.api";

export {
  useCreateModifierGroupMutation,
  useDeleteModifierGroupMutation,
  useUpdateModifierGroupMutation,
  useFetchModifierGroupDetailsQuery,
  useFetchModifierGroupsQuery,
  useFetchAllModifierGroupsQuery,
} from "./apis/modifier-group.api";

export {
  useCreateItemMutation,
  useDeleteItemMutation,
  useFetchItemDetailsQuery,
  useFetchItemsQuery,
  useLazyFetchItemDetailsQuery,
  useUpdateItemMutation,
  useToggleItemStockMutation,
  useUpdateItemImageMutation,
} from "./apis/item.api";

export {
  useFetchAllHeadersQuery,
  useCreateHeaderMutation,
  useFetchHeaderDetailsQuery,
  useFetchHeadersQuery,
  useUpdateHeaderMutation,
  useToggleHeaderIsActiveStatusMutation,
  useDeleteHeaderMutation,
} from "./apis/header.api";

export {
  useFetchAllRidersQuery,
  useFetchRidersQuery,
  useFetchRiderDetailsQuery,
  useCreateRiderMutation,
  useUpdateRiderMutation,
  useDeleteRiderMutation,
} from "./apis/rider.api";

export {
  useFetchOrdersQuery,
  useFetchRushOrdersQuery,
  useFetchOrderDetailsQuery,
  useUpdateOrderStatusMutation,
  useUpdateOrderRiderMutation,
  useFetchRushOrdersListQuery,
} from "./apis/order.api";

export {
  useFetchRiderRequestsQuery,
  useDeleteRiderRequestMutation,
} from "./apis/rider-request.api";

export {
  useFetchRestaurantRequestsQuery,
  useDeleteRestaurantRequestMutation,
} from "./apis/restaurant-request.api";

export {
  useCreateAdMutation,
  useUpdateAdMutation,
  useDeleteAdMutation,
  useFetchAdsQuery,
  useFetchAdDetailsQuery,
  useToggleAdIsActiveStatusMutation,
} from "./apis/ad.api";
